import { useDraggingStore } from "@/stores/draggingStore";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useCommentsStore } from "@/stores/commentsStore";
import { useResizeColumnsStore } from "@/stores/resizeColumnsStore";
import { useNotificationsStore } from "@/stores/notifications";
import { TopolBlock } from "@/schemas/templateSchema";
import { useClipboard } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { computed, ref, onMounted, onBeforeUnmount } from "vue";

interface Props {
  block: TopolBlock;
  columnIndex: number;
  sectionIndex: number;
  blockIndex: number;
  isLocked: boolean;
}

export default function blockProperties(props: Props) {
  const mjmlStore = useMjmlStore();
  const draggingStore = useDraggingStore();
  const commentsStore = useCommentsStore();
  const resizeColumnsStore = useResizeColumnsStore();
  const notificationsStore = useNotificationsStore();
  const { t } = useI18n();
  const { copy, copied } = useClipboard();

  const isHovered = ref(false);
  const blockHeight = ref(0);
  const blockWidth = ref(0);
  const showPaste = ref(false);

  const id = computed(() => {
    const tagNameParts = props.block.tagName.split("-");
    let blockType = tagNameParts[1];

    if (blockType === "image") {
      blockType = "img";
    }

    return `${blockType}${props.block.uid}`;
  });

  const bluredOrNotHidden = computed(() => {
    if (!props.block) return false;
    if (isHidden.value) {
      if (!mjmlStore.blurHiddenElements) {
        return true;
      }

      return false;
    }

    return true;
  });

  const hiddenStyle = computed(() => {
    if (isHidden.value && !mjmlStore.blurHiddenElements) {
      return {
        opacity: "25%",
      };
    }

    return {};
  });

  const isActive = computed(() => {
    return mjmlStore.isActiveBlock({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      blockIndex: props.blockIndex,
    });
  });
  const simulatedHover = computed(() => {
    const isHoverSimulation = mjmlStore.simulateHoverToUid === props.block.uid;
    if (isHoverSimulation) {
      scrollToView();
    }
    return isHoverSimulation;
  });

  const showActions = computed(() => {
    const isResizing = resizeColumnsStore.isResizing;
    if (!isResizing) {
      return isHovered.value || simulatedHover.value;
    }
    const resizingColumn = resizeColumnsStore.resizingColumn;
    return (
      resizingColumn.sectionIndex === props.sectionIndex &&
      resizingColumn.columnIndex === props.columnIndex &&
      resizingColumn.blockIndex === props.blockIndex
    );
  });

  const disableUpMovement = computed(() => {
    return mjmlStore.isFirstBlockInColumn({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      blockIndex: props.blockIndex,
    });
  });

  const disableDownMovement = computed(() => {
    return mjmlStore.isLastBlockInColumn({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      blockIndex: props.blockIndex,
    });
  });

  const columnPaddingX = computed(() => {
    const column =
      mjmlStore.getMjml().children[0].children[props.sectionIndex].children[
        props.columnIndex
      ];

    if (column && column.attributes && column.attributes.padding) {
      const padRaw = column.attributes.padding;
      const padding = padRaw.split(" ");
      return parseInt(padding[1]) + parseInt(padding[3]);
    }
    return 0;
  });

  const columnWidth = computed(() => {
    const column =
      mjmlStore.getMjml().children[0].children[props.sectionIndex].children[
        props.columnIndex
      ];

    if (column && column.attributes && column.attributes.width) {
      return Number(column.attributes.width.replace("%", ""));
    }
    return 0;
  });

  const isLastColumn = computed(() => {
    const columnsCount =
      mjmlStore.getMjml().children[0].children[props.sectionIndex].children
        .length;

    return columnsCount - 1 === props.columnIndex;
  });

  const isHidden = computed(() => {
    if (mjmlStore.isMobileFirstEnabled) {
      const cssClass = props.block.attributes["css-class"];
      const isMobileView = mjmlStore.isMobileView;

      if (cssClass === "hide_on_mobile" && isMobileView) {
        return true;
      } else if (cssClass === "hide_on_desktop" && !isMobileView) {
        return true;
      }
    }

    return false;
  });

  const dragging = computed(() => {
    return draggingStore.dragging;
  });

  const isCurrentElementDragged = computed(() => {
    const dragOptions = draggingStore.dragStart;

    if (
      dragOptions &&
      props.sectionIndex !== null &&
      props.sectionIndex !== undefined &&
      props.columnIndex !== null &&
      props.columnIndex !== undefined &&
      props.blockIndex !== null &&
      props.blockIndex !== undefined
    ) {
      const ret =
        props.sectionIndex === dragOptions.sectionIndex &&
        props.columnIndex === dragOptions.columnIndex &&
        props.blockIndex === dragOptions.blockIndex;
      return ret;
    }

    return false;
  });

  const showDragBorder = computed(() => {
    return dragging.value && !isCurrentElementDragged.value;
  });

  async function mouseOver() {
    if (isHovered.value === false) {
      window.$event.emit("blockHovered");
    }
    isHovered.value = true;

    const el = document.getElementById(`${id.value}`);

    if (el) {
      blockHeight.value = el.offsetHeight;
    }

    blockWidth.value = countBlockWidth();

    showPaste.value = true;
  }

  function countBlockWidth() {
    if (mjmlStore.isMobileView && mjmlStore.isMobileFirstEnabled) {
      const columnsCount =
        mjmlStore.mjml.children[0].children[props.sectionIndex].children.length;

      return 375 / columnsCount;
    }

    const lefPaneWidth = 370;

    const editorViewWidth = window.innerWidth - lefPaneWidth;

    const column =
      mjmlStore.mjml.children[0].children[props.sectionIndex].children[
        props.columnIndex
      ];

    let blockPercentWidthOfColumn = 0;

    if (column && column && column.attributes && column.attributes.width) {
      blockPercentWidthOfColumn = parseInt(column.attributes.width);
    }

    const widthNum = blockPercentWidthOfColumn / 100;

    const columnWidth = props.block.attributes["containerWidth"] / widthNum;

    if (columnWidth < editorViewWidth) {
      return props.block.attributes["containerWidth"];
    } else {
      return editorViewWidth * widthNum;
    }
  }

  function mouseOut() {
    if (isHovered.value) {
      window.$event.emit("blockHoveredOff");
    }

    isHovered.value = false;
  }

  function click(productTab = "settings") {
    if (!isActive.value) {
      mjmlStore.selectBlock({
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: props.blockIndex,
      });
    }
    if (productTab && typeof productTab === "string") {
      mjmlStore.productTab = productTab;
    }
  }

  function copyBlock() {
    mjmlStore.duplicateBlock({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      blockIndex: props.blockIndex,
    });
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.readText();
      copy(
        JSON.stringify(
          mjmlStore.getBlockByIndexes({
            sectionIndex: props.sectionIndex,
            columnIndex: props.columnIndex,
            blockIndex: props.blockIndex,
          })
        )
      );
    } catch (e) {
      notificationsStore.createNotification({
        type: "error",
        text: t("layout.clipboard_not_allowed"),
      });
      return;
    }
  }

  function deleteBlock() {
    mjmlStore.deleteBlock({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      blockIndex: props.blockIndex,
    });
  }

  function moveUp() {
    mjmlStore.reorderBlocks({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      oldIndex: props.blockIndex,
      newIndex: props.blockIndex - 1,
      arrowMovement: true,
    });
  }

  function moveDown() {
    mjmlStore.reorderBlocks({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      oldIndex: props.blockIndex,
      newIndex: props.blockIndex + 1,
      arrowMovement: true,
    });
  }

  function scrollToView() {
    const el = document.getElementById(`${id.value}`);
    if (el) {
      const rect = el.getBoundingClientRect();
      const offset = rect.top + window.scrollY - 100;
      if (
        !(
          rect.top < window.innerHeight &&
          rect.top > 52 &&
          rect.bottom > 0 &&
          rect.bottom < window.innerHeight
        )
      ) {
        window.scrollTo({
          top: offset,
          behavior: "smooth",
        });
      }
    }
  }

  function onConversationHoveredOn() {
    if (
      isHovered.value === false &&
      commentsStore.hoverOverElement({
        type: "block",
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: props.blockIndex,
      })
    ) {
      isHovered.value = true;
    }
  }

  function onConversationHoveredOff() {
    if (isHovered.value === true) {
      isHovered.value = false;
    }
  }

  onMounted(() => {
    window.$event.on("conversationHoveredOn", onConversationHoveredOn);
    window.$event.on("conversationHoveredOff", onConversationHoveredOff);
  });

  onBeforeUnmount(() => {
    window.$event.off("conversationHoveredOn", onConversationHoveredOn);
    window.$event.off("conversationHoveredOff", onConversationHoveredOff);
    mouseOut();
  });

  return {
    showActions,
    isHovered,
    simulatedHover,
    blockHeight,
    blockWidth,
    id,
    bluredOrNotHidden,
    hiddenStyle,
    isActive,
    disableUpMovement,
    disableDownMovement,
    columnPaddingX,
    columnWidth,
    isLastColumn,
    isHidden,
    dragging,
    isCurrentElementDragged,
    showDragBorder,
    showPaste,
    copied,
    // deselect,
    mouseOver,
    countBlockWidth,
    mouseOut,
    click,
    copyBlock,
    deleteBlock,
    moveUp,
    moveDown,
    copyToClipboard,
  };
}
